import type { ChipIntent } from '@/components/common/chip';
import { formatDeviceType } from '@/features/devices';
import { formatInAppTz } from '@/lib/date';
import type { PreviewFeature } from '@/types';

import type {
  Address,
  CustomerActivityType,
  CustomerConnectorStatus,
  CustomerDeviceStatus,
  CustomerDeviceType,
  CustomerStatus,
  CustomerSubscriptionStatus,
} from './types';

/** One-line postal address for the Customer Detail header (`line1[, line2], city, state zipcode`). */
export function formatAddressLine(address: Address): string {
  const parts = [address.line1, address.line2, address.city].filter(Boolean);
  return `${parts.join(', ')}, ${address.state} ${address.zipcode}`;
}

/** Status label + Figma colorway. The `<Chip>` intents render the exact Figma hex via design
 *  tokens (no hardcoded hex): approved = #A7F0BA/#3D5B34, attention = #FFDFDF/#A2191F,
 *  review = #FDDC69/#684E00. `pending_activation` uses `review` (the exact amber), not `wip`. */
export const CUSTOMER_STATUS_CHIP: Record<CustomerStatus, { label: string; intent: ChipIntent }> = {
  active: { label: 'Active', intent: 'approved' },
  inactive: { label: 'Inactive', intent: 'attention' },
  pending_activation: { label: 'Activation Pending', intent: 'review' },
};

/** Activity Log Type → display label (README's 9 FE-owned values). Plain text in the table
 *  (Figma leaf `622-195428`: Type is `Text/Primary` plain text, not a chip). */
export const CUSTOMER_ACTIVITY_TYPE_LABEL: Record<CustomerActivityType, string> = {
  device: 'Device',
  installation: 'Installation',
  subscription: 'Subscription',
  warranty: 'Warranty',
  replacement: 'Replacement',
  order: 'Order',
  profile: 'Profile',
  status: 'Status',
  support_ticket: 'Support Ticket',
};

/** Log Date & Time — "MMM D, YYYY | h:mma" e.g. "Jan 12, 2026 | 3:43pm" (README AC; ADR-0007).
 *  Distinct from the shipped day-first `D MMM YYYY | hh:mm A` formatters. */
export function formatActivityLogDateTime(iso: string): string {
  return formatInAppTz(iso, 'MMM D, YYYY | h:mma');
}

/** Device Status label + Figma colorway (intents confirmed via Figma get_variable_defs:
 *  approved=green, review=amber, neutral=grey, published=blue, attention=red). ADO 112576 names:
 *  Active → Running, Idle → Off, plus the new Pending Confirmation. */
export const DEVICE_STATUS_CHIP: Record<
  CustomerDeviceStatus,
  { label: string; intent: ChipIntent }
> = {
  pending_configuration: { label: 'Pending Configuration', intent: 'neutral' },
  pending_confirmation: { label: 'Pending Confirmation', intent: 'neutral' },
  pending_qc: { label: 'Pending QC', intent: 'review' },
  running: { label: 'Running', intent: 'approved' },
  off: { label: 'Off', intent: 'published' },
  disconnected: { label: 'Disconnected', intent: 'attention' },
};

/** Device Type options for the two filter drawers that offer them — the Devices tab (ADO 119225)
 *  and Subscription Details. Homed here rather than module-local in one drawer so the two tabs
 *  cannot drift: labels come from `formatDeviceType`, so "DM Device" / "CropX" / "Other" read
 *  identically on both. The three values are `CONTEXT.md`'s Device Type in full; CropX is tagged
 *  out of the release scope (ADR-0034, ADO 121238), so consumers pass it through `onlyEnabled`. */
export const DEVICE_TYPE_OPTIONS: {
  value: CustomerDeviceType;
  label: string;
  feature?: PreviewFeature;
}[] = (['dm_device', 'cropx', 'other'] as const).map((value) => ({
  value,
  label: formatDeviceType(value),
  ...(value === 'cropx' ? { feature: 'cropx' as const } : {}),
}));

/** Connector Status label + colorway (story 113560). Follows the house semantics the Device
 *  Status map already uses: a pending step is neutral, a healthy end state is green, a failure is
 *  red. Figma left the badge treatment undefined ("visual treatment to be defined in design"). */
export const CONNECTOR_STATUS_CHIP: Record<
  CustomerConnectorStatus,
  { label: string; intent: ChipIntent }
> = {
  pending: { label: 'Pending', intent: 'neutral' },
  connected: { label: 'Connected', intent: 'approved' },
  failed: { label: 'Failed', intent: 'attention' },
};

/** Subscription Status label + colorway. `active`/`expired` (ADO 112576 renamed Inactive to
 *  Expired). Shared by the Subscription Details and Devices tabs. */
export const SUBSCRIPTION_STATUS_CHIP: Record<
  CustomerSubscriptionStatus,
  { label: string; intent: ChipIntent }
> = {
  active: { label: 'Active', intent: 'approved' },
  expired: { label: 'Expired', intent: 'attention' },
};

/** Last Active: "-" for Running / Pending Configuration / Pending Confirmation / Pending QC; the
 *  timestamp on the application (Eastern) clock for Off / Disconnected (ADO 120986). Null timestamp
 *  also renders "-". */
export function formatLastActive(status: CustomerDeviceStatus, lastActive: string | null): string {
  if (status !== 'off' && status !== 'disconnected') return '-';
  return lastActive ? formatInAppTz(lastActive, 'MMM D, YYYY | h:mma') : '-';
}

/** Order History Payment Method — wire values match the Payments Ledger's own
 *  `paymentMethodSchema.type` enum (`card` / `net_banking`; ADR-0017), not the invented
 *  Title-Case strings the filter previously sent (ADO 119210/119212). The filter's options and
 *  the table's own display both read this one map so they cannot drift again. */
export const PAYMENT_METHOD_LABEL: Record<string, string> = {
  card: 'Credit Card',
  net_banking: 'Net Banking',
};

export const PAYMENT_METHOD_OPTIONS = Object.entries(PAYMENT_METHOD_LABEL).map(
  ([value, label]) => ({ value, label }),
);

/** Falls back to the raw wire value for a payment method the map doesn't know yet, rather than
 *  rendering blank. Named distinctly from `payments`' `formatPaymentMethod` (which formats a
 *  structured `{type, brand, last4, bankName}` object, not a bare wire string). */
export function formatOrderPaymentMethod(value: string | null): string {
  if (!value) return '-';
  return PAYMENT_METHOD_LABEL[value] ?? value;
}
